/**
 * @file Widget state management for the getDirectionsFromTo plugin
 *
 * This module manages the centralized state for the directions widget using
 * the WidgetState pattern. State updates trigger registered callbacks after
 * the microtask queue is flushed, allowing multiple synchronous updates to
 * batch before callbacks fire.
 *
 * ## State Flow Overview
 *
 * 1. **Initialization**: Default state is set when plugin loads
 * 2. **Search Input**: User enters origin/destination → updates `searchInputs`
 * 3. **Route Request**: `navigation/showDirectionsFromTo` triggered → updates `endpointIds`, `endpoints`
 * 4. **Lane Selection**: If route passes security → updates `securityLanes`, `immigrationLanes`
 * 5. **Route Display**: Routes calculated → updates `directRoutes`, `accessibleRoutes`, `steps`, etc.
 * 6. **Navigation**: User steps through route → updates `currentStep`, `currentStop`
 * 7. **Cleanup**: Route ended → state reset to default
 *
 * ## Callback Registration
 *
 * Callbacks registered via `widgetState.addCallback(fn)` are called after
 * every state update (batched via microtask). Common callbacks:
 * - `showNavlinesAsync`: Redraws navigation lines when route data changes
 * - React component re-renders via useWidgetState hook
 *
 * @see ./types.js for complete type definitions
 */

import { SecurityLaneType as PickerSteps } from '../../../wayfinder/src/wayfinder.ts';
import { RouteOptionsClassification, RouteType } from './constants.ts';

/**
 * Derives the selected security/immigration lanes from the lane arrays.
 * This is a pure function that extracts lane IDs where `default: true`.
 *
 * @param {import('./types.ts').LaneOption[]|null} securityLanes - Security lane options
 * @param {import('./types.ts').LaneOption[]|null} immigrationLanes - Immigration lane options
 * @returns {import('./types.ts').SelectedSecurityLanes} Object with selected lane IDs
 */
export const getSelectedSecurityLanes = (securityLanes, immigrationLanes) => {
  const getDefaultIds = lanes => (lanes ?? []).filter(lane => lane.default).map(lane => lane.id);
  const selected = {
    [PickerSteps.SECURITY]: getDefaultIds(securityLanes),
    [PickerSteps.IMMIGRATION]: getDefaultIds(immigrationLanes),
  };

  return Object.fromEntries(Object.entries(selected).filter(([, ids]) => Array.isArray(ids) && ids.length > 0));
};

/**
 * Default route accessibility preference, persisted across sessions
 * @type {RouteType}
 */
let defaultRouteAccessibilityChoice = RouteType.DIRECT;

/**
 * Sets the default route accessibility choice for future navigation requests.
 * This preference persists for the session and is used when no explicit
 * accessibility preference is provided.
 *
 * @param {RouteType} choice - Either 'direct' or 'accessible'
 */
export const setDefaultRouteAccessibilityChoice = choice => {
  defaultRouteAccessibilityChoice = choice;
};

/**
 * Gets the current default route accessibility choice.
 *
 * @returns {RouteType} Current default preference
 */
export const getDefaultRouteAccessibilityChoice = () => defaultRouteAccessibilityChoice;

/**
 * Returns the default/initial state for the directions widget.
 * Called on plugin initialization and when ending a route.
 *
 * @returns {import('./types.ts').DirectionsWidgetState} Initial widget state
 */
export const getDefaultState = widgetState => {
  // preserve the followig from existing state if available
  const { securityLanes, immigrationLanes, defaultSearch, overviewHeaderRef, overviewBottomBarRef } = widgetState
    ? widgetState.getState()
    : {
        securityLanes: null,
        immigrationLanes: null,
        defaultSearch: [],
        overviewHeaderRef: null,
        overviewBottomBarRef: null,
      };

  return {
    // === Search State ===
    /** @type {import('./types.ts').SearchInput[]} User input for each endpoint field. Minimum 2 (origin + destination). */
    searchInputs: [{}, {}],

    /** @type {import('./types.ts').EndpointId[]} Raw endpoint IDs as provided to showDirectionsFromTo */
    endpointIds: [],

    /** @type {import('./types.ts').NavigationEndpoint[]} Resolved endpoints with full location data */
    endpoints: [],

    defaultSearch, // list of default search results for endpoints

    // === Route Data ===
    /** @type {import('./types.ts').CalculatedRoute[]} Direct routes for each leg */
    directRoutes: [],

    /** @type {import('./types.ts').CalculatedRoute[]|null} Accessible routes (may be null) */
    accessibleRoutes: null,

    /** @type {import('./types.ts').NavigationStep[][]} Steps organized by leg index */
    steps: [],

    /** @type {import('./types.ts').RouteSegment[][]} Segments organized by leg index */
    segments: [],

    /** @type {number[]} Travel time in minutes for each leg */
    times: [],

    /** @type {number[]} Distance in meters for each leg */
    distances: [],

    // === Navigation State ===
    /** @type {number} Current step index within the current leg */
    currentStep: 0,

    /** @type {number} Current leg index in multi-point routing */
    currentStop: 0,

    /** @type {boolean} Whether user is in expanded navigation mode */
    isNavigating: false,

    // === UI State ===
    /** @type {boolean} Whether route loading finished or was canceled for editing */
    hasLoaded: false,

    /** @type {boolean} Whether endpoints, route calculation or lane selection are pending */
    isRoutePending: false,

    /** @type {boolean} Whether route calculation failed */
    isRouteUnavailable: false,

    /** @type {boolean} Whether route exits a secure area */
    leavingSecurity: false,

    /** @type {boolean} Whether we're refreshing a previously displayed route */
    wasRoutePreviouslyDisplayed: false,

    /** @type {string|null} Current floor level name for display */
    currentLevelName: null,

    /** @type {boolean} Whether to show the level name in the UI */
    showLevelName: false,

    /** @type {number} Currently focused input field index */
    currentInputIndex: 0,

    // === Route Options ===
    /** @type {RouteType} User's route preference (direct or accessible) */
    routeAccessibiltyChoice: defaultRouteAccessibilityChoice,

    /** @type {RouteOptionsClassification} Classification of available route options */
    routeTypes: RouteOptionsClassification.DISTINCT,

    // === Lane Picker State ===
    /** @type {import('./types.ts').LaneOption[]} Available security lane options (their `default` property is the checked status which should be preserved) */
    securityLanes,

    /** @type {import('./types.ts').LaneOption[]} Available immigration lane options (their `default` property is the checked status which should be preserved) */
    immigrationLanes,

    /** @type {import('./types.ts').LanePickerStep|null} Current lane picker modal step */
    lanePickerStep: null,

    /** @type {boolean} Whether user has completed lane selection */
    wereLanesPicked: false,

    /** @type {import('./types.ts').SelectedSecurityLanes|null} SDK-provided pre-selected security lanes (bypasses lane picker) */
    selectedSecurityLanes: null,

    // === Analytics & References ===
    /** @type {string|null} Source that initiated navigation (for analytics) */
    referrer: null,

    /** @type {import('./types.ts').EndpointId[]} POIs to avoid in routing */
    poisToAvoid: [],

    /** @type {React.RefObject|null} Ref for accessible toggle (focus management) */
    routeTypeAccessibleToggleRef: null,

    /** @type {React.RefObject|null} Ref for modal close button (focus management) */
    modalCloseToggleRef: null,

    /** @type {React.RefObject|null} Directions header element, published by the header so the route overview can measure it. Preserved across resets (the component stays mounted). */
    overviewHeaderRef,

    /** @type {React.RefObject|null} Bottom bar content element, published by the mobile result view so the route overview can measure it. Preserved across resets. */
    overviewBottomBarRef,
  };
};
